import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Modal, Input, Select } from "./Leads";
import { ROLE_LABELS, TEAM_ROLE_OPTIONS } from "../lib/constants";
import { useAuth } from "../lib/auth";
import { Plus, Users as UsersIcon, Pencil, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", password: "", name: "", role: "senior_associate", phone: "", designation: "", department: "", employee_id: "", reporting_manager: "", joining_date: "", status: "Active" };

export default function Team() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showMatrix, setShowMatrix] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = async () => setItems((await api.get("/users")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({ ...EMPTY, ...u, password: "" });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        const body = { ...form };
        delete body.email;
        if (!body.password) delete body.password;
        await api.put(`/users/${editing.id}`, body);
        toast.success("Member updated");
      } else {
        await api.post("/users", form);
        toast.success("Member added");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const team = items.filter((u) => u.role !== "client");
  const clients = items.filter((u) => u.role === "client");

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-emerald-700 font-semibold mb-2">Organization</p>
          <h1 className="text-4xl font-bold tracking-tight text-stone-900" style={{ fontFamily: "Bricolage Grotesque" }}>Team</h1>
        </div>
        <div className="flex gap-2">
          {me?.role === "super_admin" && (
            <button onClick={() => setShowMatrix(true)} data-testid="permission-matrix-btn" className="flex items-center gap-2 bg-white border border-stone-300 text-[#0F2A4A] px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-stone-50">
              <ShieldCheck className="w-4 h-4" /> Permission Matrix
            </button>
          )}
          <button onClick={openNew} data-testid="new-user-btn" className="flex items-center gap-2 bg-stone-900 text-white px-4 py-2.5 rounded-lg text-sm font-medium">
            <Plus className="w-4 h-4" /> Add Member
          </button>
        </div>
      </div>

      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="team-table">
          <thead className="bg-stone-50 text-left text-[10px] uppercase tracking-wider text-stone-500">
            <tr>
              <th className="px-4 py-3">Member</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3">Designation</th>
              <th className="px-4 py-3">Department</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Last Login</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-center">Edit</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {team.map((u) => (
              <tr key={u.id} className="hover:bg-stone-50/60" data-testid={`team-row-${u.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-emerald-700 flex items-center justify-center text-white text-xs font-semibold shrink-0">
                      {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-stone-900 truncate">{u.name}</div>
                      <div className="text-xs text-stone-500 truncate">{u.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-[10px] uppercase tracking-wider bg-stone-100 text-stone-700 px-2 py-1 rounded-full font-medium">{ROLE_LABELS[u.role] || u.role}</span>
                </td>
                <td className="px-4 py-3 text-xs text-stone-600">{u.designation || "—"}</td>
                <td className="px-4 py-3 text-xs text-stone-600">{u.department || "—"}</td>
                <td className="px-4 py-3 text-xs text-stone-600">{u.phone || "—"}</td>
                <td className="px-4 py-3 text-xs text-stone-500">{u.last_login_at ? new Date(u.last_login_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "Never"}</td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] uppercase px-2 py-0.5 rounded-full font-bold ${(u.status || "Active") === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{u.status || "Active"}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => openEdit(u)} data-testid={`team-edit-${u.id}`} className="p-1.5 rounded hover:bg-[#72BE44]/15 text-[#0F2A4A]"><Pencil className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div>
        <h2 className="text-lg font-bold text-stone-900 mb-3 flex items-center gap-2"><UsersIcon className="w-4 h-4 text-emerald-700" /> Client Accounts ({clients.length})</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {clients.map((u) => (
            <div key={u.id} className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-3" data-testid={`client-card-${u.id}`}>
              <div className="w-8 h-8 rounded-full bg-[#0F2A4A] flex items-center justify-center text-white text-xs font-semibold shrink-0">
                {(u.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-stone-900 text-sm truncate">{u.name}</div>
                <div className="text-xs text-stone-500 truncate">{u.email}</div>
              </div>
              <div className="flex items-center gap-1">
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded-full font-bold ${(u.status || "Active") === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-rose-100 text-rose-800"}`}>{u.status || "Active"}</span>
                <button onClick={() => openEdit(u)} data-testid={`client-edit-${u.id}`} className="p-1 rounded hover:bg-stone-100 text-stone-500"><Pencil className="w-3 h-3" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {open && (
        <Modal title={editing ? `Edit — ${editing.name}` : "Add Team Member"} onClose={() => setOpen(false)}>
          <form onSubmit={submit} className="space-y-3">
            <Input label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required testid="user-form-name" />
            {!editing && <Input label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required testid="user-email" />}
            <Input label={editing ? "New Password (leave blank to keep)" : "Password"} type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} required={!editing} testid="user-password" />
            <Select label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })}
              options={TEAM_ROLE_OPTIONS.map((k) => ({ value: k, label: ROLE_LABELS[k] }))} testid="user-role" />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Designation" value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} testid="user-designation" />
              <Input label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="user-department" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="user-phone" />
              <Input label="Employee ID" value={form.employee_id} onChange={(v) => setForm({ ...form, employee_id: v })} testid="user-employee-id" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Joining Date" type="date" value={form.joining_date} onChange={(v) => setForm({ ...form, joining_date: v })} testid="user-joining-date" />
              <Select label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })}
                options={[{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive (blocks login)" }]} testid="user-status" />
            </div>
            <button type="submit" data-testid="user-submit" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium">{editing ? "Save Changes" : "Add Member"}</button>
          </form>
        </Modal>
      )}

      {showMatrix && <PermissionMatrix onClose={() => setShowMatrix(false)} />}
    </div>
  );
}

function PermissionMatrix({ onClose }) {
  const [data, setData] = useState(null);
  const [role, setRole] = useState("hod");
  const [perms, setPerms] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/permissions").then((r) => { setData(r.data); setPerms(r.data.roles.hod || {}); });
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const pickRole = (r) => { setRole(r); setPerms(data.roles[r] || {}); };
  const toggle = (m, a) => setPerms({ ...perms, [m]: { ...(perms[m] || {}), [a]: !(perms[m] || {})[a] } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/permissions/${role}`, perms);
      const r = await api.get("/permissions");
      setData(r.data);
      toast.success(`Permissions saved for ${ROLE_LABELS[role]}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  if (!data) return null;
  const editable = ["hod", "senior_associate", "junior_associate"];

  return (
    <div className="fixed inset-0 bg-stone-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="permission-matrix-modal">
        <div className="sticky top-0 bg-[#0F2A4A] text-white px-6 py-4 flex items-center justify-between rounded-t-xl">
          <div>
            <h2 className="font-bold">Role Permission Matrix</h2>
            <p className="text-xs text-slate-300">Changes apply immediately to all users of the role</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white text-sm">Close</button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            {editable.map((r) => (
              <button key={r} onClick={() => pickRole(r)} data-testid={`matrix-role-${r}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition ${role === r ? "bg-[#0F2A4A] text-white" : "bg-stone-100 text-stone-600 hover:bg-stone-200"}`}>
                {ROLE_LABELS[r]}
              </button>
            ))}
          </div>
          <table className="w-full text-sm" data-testid="matrix-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="px-3 py-2">Module</th>
                {data.actions.map((a) => <th key={a} className="px-3 py-2 text-center capitalize">{a}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.modules.map((m) => (
                <tr key={m}>
                  <td className="px-3 py-2 font-medium text-stone-800 capitalize">{m.replace("_", " ")}</td>
                  {data.actions.map((a) => (
                    <td key={a} className="px-3 py-2 text-center">
                      <input type="checkbox" checked={!!(perms[m] || {})[a]} onChange={() => toggle(m, a)}
                        data-testid={`matrix-${m}-${a}`} className="w-4 h-4 accent-emerald-700 cursor-pointer" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={save} disabled={saving} data-testid="matrix-save-btn" className="w-full bg-emerald-700 text-white py-2.5 rounded-lg font-medium disabled:opacity-60">
            {saving ? "Saving..." : `Save Permissions for ${ROLE_LABELS[role]}`}
          </button>
        </div>
      </div>
    </div>
  );
}
